import React, { useState } from "react";
import { Topic } from "../entities/ai-topic.entity";
import { FaImage, FaTags, FaUserTie } from "react-icons/fa";
import { RxCross2 } from "react-icons/rx";
import { MenuActions } from "src/components/menu-actions";
import { useDeleteAITopicMutation } from "../ai-topic.service";

type TopicRow = Topic & { id: string };

type Props = { data: TopicRow[] };

const formatDate = (ts: any) => {
  if (!ts) return "N/A";
  return new Date(ts?.toDate?.() || ts).toLocaleDateString();
};

const formatTime = (ts: any) => {
  if (!ts) return "";
  return new Date(ts?.toDate?.() || ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

/* ───────────────────── Delete Confirmation Dialog ───────────────────── */

const DeleteConfirmationDialog: React.FC<{
  topic: TopicRow;
  onClose: () => void;
}> = ({ topic, onClose }) => {
  const [deleteTopic, { isLoading }] = useDeleteAITopicMutation();

  const handleConfirm = async () => {
    await deleteTopic({ id: topic.id });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Confirm Delete</h2>
          <button
            onClick={onClose}
            className="text-2xl text-gray-400 hover:text-gray-700 cursor-pointer"
          >
            <RxCross2 />
          </button>
        </div>

        <p className="text-gray-600 mb-2">
          Are you sure you want to delete the topic{" "}
          <span className="font-bold text-gray-900">{topic.slug.replace("_", " ").toLowerCase()}</span>?
        </p>
        <p className="text-sm text-red-500 mb-6">This action cannot be undone.</p>

        <div className="flex justify-end gap-3 border-t pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors cursor-pointer ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isLoading ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ───────────────────── Main Table ───────────────────── */

export const AITopicsTable: React.FC<Props> = ({ data }) => {
  const [deleteTarget, setDeleteTarget] = useState<TopicRow | null>(null);

  return (
    <div className="overflow-x-auto mb-6 -mx-2 sm:mx-0">
      <table className="min-w-[1000px] sm:min-w-full border border-gray-200 divide-y divide-gray-200 text-[11px] sm:text-xs">
        <thead className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white text-center text-[10px] sm:text-xs">
          <tr>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">ID</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Topic</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Category</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Roles</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Attachment</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Created At</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center py-8 text-xl text-gray-500">
                <div className="flex flex-col items-center gap-3">
                  <FaTags className="text-4xl text-gray-300" />
                  <span>No AI Topics Found</span>
                </div>
              </td>
            </tr>
          )}
          {data.map((topic) => (
            <tr
              key={topic.id}
              className="text-center odd:bg-white even:bg-gradient-to-r even:from-purple-50/30 even:to-indigo-50/30 hover:bg-purple-100/50 transition-colors duration-200"
            >
              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200">
                <span className="font-mono text-[10px] sm:text-[11px] bg-gray-100 px-2 py-1 rounded text-gray-700">
                  {topic.id}
                </span>
              </td>
              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 rounded-full font-semibold text-[11px] sm:text-xs border border-purple-200">
                  <FaTags className="text-purple-500" />
                  {topic.slug.replace("_", " ").toLowerCase()}
                </span>
              </td>
              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200">
                <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 rounded-lg font-medium text-[11px] sm:text-xs border border-blue-200">
                  {topic.category}
                </span>
              </td>
              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200">
                <div className="flex flex-wrap justify-center gap-1">
                  {topic.roles?.map((role, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-800 rounded-full text-[11px] sm:text-[11px] border border-emerald-200"
                    >
                      <FaUserTie className="text-emerald-500 text-[8px]" />
                      {role.replace("_", " ").toLowerCase()}
                    </span>
                  )) || (
                    <span className="text-gray-400 italic">No roles</span>
                  )}
                </div>
              </td>
              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200">
                {topic.attachment?.fileUrl ? (
                  <div className="flex justify-center group relative">
                    <img 
                      src={topic.attachment.fileUrl} 
                      alt={topic.slug}
                      className="w-12 h-12 object-cover rounded-lg border-2 border-white shadow-md group-hover:scale-150 transition-transform duration-200 z-0 group-hover:z-10 cursor-pointer bg-gray-100"
                      onClick={() => window.open(topic.attachment.fileUrl, '_blank')}
                    />
                  </div>
                ) : (
                  <span className="text-gray-400 italic text-[10px]">No attachment</span>
                )}
              </td>
              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200">
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] sm:text-[11px] font-semibold text-gray-700">{formatDate(topic.createdAt)}</span>
                  <span className="text-[9px] sm:text-[10px] text-gray-500">{formatTime(topic.createdAt)}</span>
                </div>
              </td>
              <td className="px-2 py-3 border-t border-gray-200">
                <MenuActions actions={[{
                  type: "BUTTON",
                  label: "Delete",
                  onClick: () => setDeleteTarget(topic),
                  color: "danger"
                }]} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <DeleteConfirmationDialog
          topic={deleteTarget}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default AITopicsTable;
